import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Trophy, Users, TrendingUp, MessageSquare, ChevronRight, 
  Award, Target, Send, Clock, ArrowLeft, Eye, FileText 
} from 'lucide-react';

import StatCard from './StatCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CoachTeamStats = ({ teamId, teamName, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerSessions, setPlayerSessions] = useState([]);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [statCardPlayer, setStatCardPlayer] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({
    comment: '',
    video_timestamp: '',
    focus_area: '',
    priority: 'normal'
  });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    if (teamId) {
      fetchTeamData();
    }
  }, [teamId]);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const [leaderboardRes, statsRes] = await Promise.all([
        axios.get(`${API}/coach/team/${teamId}/leaderboard`),
        axios.get(`${API}/coach/team/${teamId}/game-stats`)
      ]);
      setLeaderboard(leaderboardRes.data.leaderboard);
      setTeamStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerSessions = async (playerId) => {
    setPlayerLoading(true);
    try {
      const response = await axios.get(`${API}/coach/player/${playerId}/game-stats`);
      setPlayerSessions(response.data.sessions);
      setSelectedPlayer({ id: playerId, name: response.data.player_name });
    } catch (error) {
      toast.error('Failed to load player sessions');
    } finally {
      setPlayerLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!selectedSession || !feedbackForm.comment.trim()) {
      toast.error('Please enter feedback comment');
      return;
    }

    setSubmittingFeedback(true);
    try {
      const response = await axios.post(`${API}/coach/game-sessions/${selectedSession.id}/feedback`, feedbackForm);
      toast.success('Feedback sent to player!');
      setFeedbackForm({ comment: '', video_timestamp: '', focus_area: '', priority: 'normal' });
      
      // Update selectedSession with the new feedback immediately
      setSelectedSession(prev => ({
        ...prev,
        coach_feedback: [...(prev.coach_feedback || []), response.data.feedback]
      }));
      
      // Also refresh the full session list
      await fetchPlayerSessions(selectedPlayer.id);
    } catch (error) {
      toast.error('Failed to send feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'bg-slate-500';
    if (grade.startsWith('A')) return 'bg-green-500';
    if (grade.startsWith('B')) return 'bg-blue-500';
    if (grade.startsWith('C')) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatPosition = (pos) => {
    return pos?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white">Loading team stats...</div>
      </div>
    );
  }

  // Session Detail View
  if (selectedSession) {
    return (
      <div className="space-y-4">
        <Button 
          variant="ghost" 
          className="text-slate-400 hover:text-white"
          onClick={() => setSelectedSession(null)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Sessions
        </Button>

        {/* Session Overview */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg text-white">
                  {selectedPlayer?.name} vs. {selectedSession.opponent_name}
                </CardTitle>
                <CardDescription className="text-slate-400">
                  {selectedSession.game_date} • {formatPosition(selectedSession.position)}
                </CardDescription>
              </div>
              {selectedSession.ai_feedback && (
                <Badge className={`${getGradeColor(selectedSession.ai_feedback.grade)} text-white`}>
                  Grade: {selectedSession.ai_feedback.grade}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Skill success rates for the tracked skills */}
            {selectedSession.derived_stats?.skills?.length > 0 && (
              <div className="mb-4 space-y-3" data-testid="coach-skill-breakdown">
                {selectedSession.derived_stats.skills.map((skill) => (
                  <div key={skill.key} data-testid={`coach-skill-${skill.key}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-300">{skill.label}</span>
                      <span className="text-xs font-semibold text-white tabular-nums">
                        {skill.successes}/{skill.attempts}
                        <span className="text-slate-400 ml-2">{Math.round(skill.success_rate)}%</span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-700 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          skill.success_rate >= 70
                            ? 'bg-green-500'
                            : skill.success_rate >= 55
                            ? 'bg-amber-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.max(skill.success_rate, 2)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Raw Stats Grid */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
              {Object.entries(selectedSession.stats || {})
                .filter(([, value]) => value > 0)
                .map(([key, value]) => (
                  <div key={key} className="bg-slate-700/50 rounded-lg p-2 text-center">
                    <div className="text-lg font-bold text-white">{value}</div>
                    <div className="text-xs text-slate-400">
                      {selectedSession.stat_labels?.[key] ||
                        key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </div>
                  </div>
                ))}
            </div>

            {/* AI Feedback */}
            {selectedSession.ai_feedback && (
              <div className="space-y-2 mb-4">
                <h4 className="text-sm font-medium text-slate-300">AI Analysis</h4>
                {selectedSession.ai_feedback.kudos?.map((k, i) => (
                  <div key={`kudo-${k.slice(0, 20)}-${i}`} className="text-sm text-green-400 bg-green-500/10 p-2 rounded">
                    ✓ {k}
                  </div>
                ))}
                {selectedSession.ai_feedback.growth_insights?.map((g, i) => (
                  <div key={`insight-${g.slice(0, 20)}-${i}`} className="text-sm text-blue-400 bg-blue-500/10 p-2 rounded">
                    → {g}
                  </div>
                ))}
              </div>
            )}

            {/* Existing Coach Feedback */}
            {selectedSession.coach_feedback?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-slate-300 mb-2">Previous Feedback</h4>
                <div className="space-y-2">
                  {selectedSession.coach_feedback.map((fb) => (
                    <div key={fb.created_at || fb.comment} className="bg-slate-700/50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-amber-400">{fb.coach_name}</span>
                        <span className="text-xs text-slate-500">
                          {new Date(fb.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300">{fb.comment}</p>
                      {fb.video_timestamp && (
                        <Badge variant="outline" className="mt-2 text-xs">
                          <Clock className="w-3 h-3 mr-1" /> {fb.video_timestamp}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Feedback Form */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              Add Feedback
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-300">Comment</Label>
              <Textarea
                data-testid="coach-feedback-comment"
                placeholder="Enter your feedback for the player..."
                value={feedbackForm.comment}
                onChange={(e) => setFeedbackForm({...feedbackForm, comment: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Video Timestamp (optional)</Label>
                <Input
                  placeholder="e.g., 2:34"
                  value={feedbackForm.video_timestamp}
                  onChange={(e) => setFeedbackForm({...feedbackForm, video_timestamp: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Priority</Label>
                <Select 
                  value={feedbackForm.priority}
                  onValueChange={(v) => setFeedbackForm({...feedbackForm, priority: v})}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="low" className="text-white">Low</SelectItem>
                    <SelectItem value="normal" className="text-white">Normal</SelectItem>
                    <SelectItem value="high" className="text-white">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Focus Area (optional)</Label>
              <Input
                placeholder="e.g., Footwork, Decision making"
                value={feedbackForm.focus_area}
                onChange={(e) => setFeedbackForm({...feedbackForm, focus_area: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>

            <Button
              className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
              onClick={handleSubmitFeedback}
              data-testid="send-coach-feedback-btn"
              disabled={submittingFeedback || !feedbackForm.comment.trim()}
            >
              <Send className="w-4 h-4 mr-2" />
              {submittingFeedback ? 'Sending...' : 'Send Feedback'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Recruiter Stat Card View
  if (statCardPlayer) {
    return (
      <StatCard
        playerId={statCardPlayer.id}
        onBack={() => setStatCardPlayer(null)}
      />
    );
  }

  // Player Sessions View
  if (selectedPlayer) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <Button 
            variant="ghost" 
            className="text-slate-400 hover:text-white"
            onClick={() => { setSelectedPlayer(null); setPlayerSessions([]); }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Leaderboard
          </Button>
          <Button
            variant="outline"
            className="border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300"
            onClick={() => setStatCardPlayer(selectedPlayer)}
            data-testid="coach-recruiter-card-btn"
          >
            <FileText className="w-4 h-4 mr-2" /> Recruiter Card
          </Button>
        </div>

        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              {selectedPlayer.name}'s Game Sessions
            </CardTitle>
            <CardDescription className="text-slate-400">
              {playerSessions.length} sessions recorded
            </CardDescription>
          </CardHeader>
          <CardContent>
            {playerLoading ? (
              <div className="text-slate-400 py-4">Loading sessions...</div>
            ) : playerSessions.length === 0 ? (
              <div className="text-slate-400 py-4">No game sessions recorded yet</div>
            ) : (
              <div className="space-y-2">
                {playerSessions.map((session) => (
                  <button
                    key={session.id}
                    data-testid={`coach-session-${session.id}`}
                    onClick={() => setSelectedSession(session)}
                    className="w-full flex items-center justify-between p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <div className="text-left">
                      <div className="font-medium text-white">vs. {session.opponent_name}</div>
                      <div className="text-sm text-slate-400">
                        {session.game_date} • {formatPosition(session.position)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!session.is_complete && (
                        <Badge variant="outline" className="text-slate-400 border-slate-500">
                          In progress
                        </Badge>
                      )}
                      {session.is_complete && session.ai_feedback && (
                        <Badge className={`${getGradeColor(session.ai_feedback.grade)} text-white`}>
                          {session.ai_feedback.grade}
                        </Badge>
                      )}
                      {session.coach_feedback?.length > 0 && (
                        <Badge variant="outline" className="text-purple-400 border-purple-400">
                          <MessageSquare className="w-3 h-3 mr-1" />
                          {session.coach_feedback.length}
                        </Badge>
                      )}
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Team Leaderboard View (default)
  return (
    <div className="space-y-4">
      {onBack && (
        <Button variant="ghost" className="text-slate-400 hover:text-white" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      )}

      {/* Team Overview */}
      <Card className="bg-slate-800/90 border-slate-700">
        <CardHeader>
          <CardTitle className="text-xl text-white flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-400" />
            {teamName || 'Team'} Performance
          </CardTitle>
          <CardDescription className="text-slate-400">
            {teamStats?.total_sessions || 0} total game sessions • {leaderboard.length} players
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Leaderboard */}
      <Card className="bg-slate-800/90 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-400" />
            Player Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          {leaderboard.length === 0 ? (
            <div className="text-slate-400 py-4 text-center">
              No player stats recorded yet
            </div>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((player, idx) => (
                <button
                  key={player.player_id}
                  data-testid={`leaderboard-player-${player.player_id}`}
                  onClick={() => fetchPlayerSessions(player.player_id)}
                  className="w-full flex items-center justify-between p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                      idx === 0 ? 'bg-yellow-500' : 
                      idx === 1 ? 'bg-slate-400' : 
                      idx === 2 ? 'bg-orange-600' : 
                      'bg-slate-600'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-white">{player.player_name}</div>
                      <div className="text-sm text-slate-400">
                        {formatPosition(player.primary_position)} • {player.total_games} games
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`${getGradeColor(player.average_grade)} text-white`}>
                      Avg: {player.average_grade}
                    </Badge>
                    <div className="text-right">
                      <div className="text-lg font-bold text-amber-400">{player.total_points}</div>
                      <div className="text-xs text-slate-500">points</div>
                    </div>
                    <Eye className="w-4 h-4 text-slate-500" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CoachTeamStats;
